import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { countryDefaultValue } from '@models/address/address';
import { Company } from "@models/companies/company";
import { Document } from "@models/documents/document";
import { DocumentationType } from "@models/documents/document-enums";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { MatchesSelected } from "@models/ui-models/bulk-change-data";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { CountriesService } from "@shared/services/countries.service";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { forkJoin, map } from "rxjs";
import { CompanyChangeAddress, customForm484A1Errors } from "../../../models/company/company-change-address.model";
import {
  AddressAndOccupierFormGroupComponent,
  AddressAndOccupierFormGroupControls
} from "../../../reusable-form-groups/address-and-occupier-form-group/address-and-occupier-form-group.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import {
  BaseBulkStepperFormComponent,
  StepperFormComponent,
  StepperFormDescriptionComponent
} from "../../stepper-form";
import { IBulkAppliedChange } from "../base-bulk-stepper-form/base-bulk-stepper-form.component";
import { getBulkCompanyChangeAddressDocumentSelection } from "./bulk-company-change-address-document-selection";
import { BulkCompanyChangeAddressStepsEnum } from "./bulk-company-change-address-steps.enum";

export interface BulkChangeAddressFormGroupControls extends AddressAndOccupierFormGroupControls {
  documentationType: FormControl<DocumentationType | null>;
  applyToRegistered: FormControl<boolean | null>;
  applyToPrincipal: FormControl<boolean | null>;
  changeDate: FormControl<Date>;
}

export type BulkChangeAddressFormGroup = FormGroup<BulkChangeAddressFormGroupControls>;
const registeredAddressBulkLabel = 'Registered Address';
const principalAddressBulkLabel = 'Principal Place of Business';

export interface IBulkChangeEntity {
  entity: Company;
  match: MatchesSelected;
}

@Component({
  selector: 'app-bulk-company-change-address',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    DisclaimerComponent,
    RouterLink,
    AddressAndOccupierFormGroupComponent,
    RadioComponent,
    ListGridClientSideComponent
  ],
  providers: [],
  templateUrl: 'bulk-company-change-address.component.html',
  styleUrls: [
    '../../company/company-address-change/company-change-address.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
    'bulk-company-change-address.component.scss'
  ],
})
export class BulkCompanyChangeAddressComponent extends BaseBulkStepperFormComponent<BulkCompanyChangeAddressStepsEnum, CompanyChangeAddress> implements OnInit {
  cdr = inject(ChangeDetectorRef);

  @Input() denyMinutesDocumentType = false;

  readonly customErrors = customForm484A1Errors;
  readonly addressInfoMessage = `A non-Australian address cannot be utilised as a registered address, principal place of business, or a director address for a sole director company.`;
  override readonly StepsEnum = BulkCompanyChangeAddressStepsEnum;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  bulkMatchesSelected: MatchesSelected[] = [];
  bulkChangeEntities: IBulkChangeEntity[] = [];

  isIndividual = false;
  trustOnly = false;

  form: BulkChangeAddressFormGroup = new FormGroup({
    ...AddressAndOccupierFormGroupComponent.defineFormControls(),
    documentationType: new FormControl<DocumentationType>(DocumentationType.Minutes, [Validators.required]),
    applyToRegistered: new FormControl<boolean | null>(null),
    applyToPrincipal: new FormControl<boolean | null>(null),
  }) as BulkChangeAddressFormGroup;

  override stepperForm = new FormGroup({
    [BulkCompanyChangeAddressStepsEnum.FormDescription]: new FormGroup({}),
    [BulkCompanyChangeAddressStepsEnum.NewAddress]: this.form,
    [BulkCompanyChangeAddressStepsEnum.Summary]: this.form,
  });

  constructor() {
    super();
    this.setupSteps(BulkCompanyChangeAddressStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDefaultMinDateStruct();
    this.maxDate = DatepickerHelper.getTodayStruct();

    this.setupChange();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);
    this.loadCompanies();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeAddress);
  }

  override setupChange(change: CompanyChangeAddress = this.formModel): void {
    const documentationType = this.trustOnly ? DocumentationType.Default : DocumentationType.Minutes;

    if (this.documentTypeOptions.some((option) => option.value == documentationType)) {
      this.form.controls.documentationType.setValue(documentationType);
    } else {
      const newDocumentationType = this.documentTypeOptions[0].value as DocumentationType;
      this.form.controls.documentationType.setValue(newDocumentationType);
    }

    if (this.trustOnly) {
      this.currentStepIndex = 1;
      this.currentStep = this.steps[this.currentStepIndex].step;
    }

    if (change === this.formModel)
      return;

    change.address.country = change.address.country || countryDefaultValue;
    this.form.patchValue({
      ...change,
      companyHaveOccupiersConsent: !!this.formModel.occupierNameIfDoesntOccupy.trim(),
    });
    this.cdr.detectChanges();

    if (this.isIndividual) {
      setControlDisabled(this.form.controls.occupiesTheAddress);
      setControlDisabled(this.form.controls.occupierNameIfDoesntOccupy);
    }
  }

  override updateDocumentationTypeOptions(): void {
    if (this.denyMinutesDocumentType) {
      this.documentTypeOptions = this.documentTypeOptions.filter((option) => option.value != DocumentationType.Minutes);
      this.form.controls.documentationType.setValue(this.documentTypeOptions[0].value as DocumentationType);
    }
  }

  override buildDocument(): Document[] | null {
    if (this.areAllRowsHaveErrors) return null;

    const formDate = new Date(this.form.value.changeDate as Date | string);
    const changeDate = DatepickerHelper.buildDateString(formDate);
    const documentationType = this.form.controls.documentationType.value!;
    const bulkCompanyChangeAddressDocuments = this.bulkChangeEntities
      .map((bulkChangeEntity, entityIndex) => ({ ...bulkChangeEntity, row: this.rows[entityIndex] }))
      .filter((bulkChangeEntityWithRow) => !bulkChangeEntityWithRow.row.error)
      .map(({ entity, match, row }) => {
        const isEntityTrust = !match.acn?.trim();
        const change = new CompanyChangeAddress({
          ...this.form.value as Partial<CompanyChangeAddress>,
          changeDate,
          applyToRegistered: match?.matches?.some(match => match.label === registeredAddressBulkLabel),
          applyToPrincipal: match?.matches?.some(match => match.label === principalAddressBulkLabel),
          relationshipIds: match.relationshipIds,
        });
        const changeAuthorisation = this.getChangeAuthorisation(entity, documentationType, row, changeDate);
        const documentType = isEntityTrust ? 't:484' : 'c:484';

        changeAuthorisation.meetingDate = changeDate;
        changeAuthorisation.documentSelection = getBulkCompanyChangeAddressDocumentSelection(changeAuthorisation.documentationType);

        return new Document({
          changes: [change],
          entityId: match.entityId,
          type: documentType,
          documentId: this.companyChangeData?.documentId,
          changeAuthorisation: match.acn ? changeAuthorisation : undefined,
          documentationType,
        });
      });

    return bulkCompanyChangeAddressDocuments;
  }

  onStepChange(stepIndex: BulkCompanyChangeAddressStepsEnum): void {
    if (stepIndex === BulkCompanyChangeAddressStepsEnum.Summary) {
      this.fillRowsErrors();
    }
  }

  loadCompanies(): void {
    this.isLoading.set(true);

    const selectedEntitiesLoadRequests = this.bulkMatchesSelected
      .map((match) => {
        const entityId = match.entityId;

        return this.loadCompany(entityId)
          .pipe(
            map((entity) => {
              return { entity, match };
            })
          );
      });

    forkJoin(selectedEntitiesLoadRequests)
      .subscribe({
        next: (bulkChangeEntities) => {
          this.bulkChangeEntities = bulkChangeEntities.filter(({ entity }) => entity) as IBulkChangeEntity[];
          this.fillRows(this.bulkChangeEntities.map(({ entity }) => entity));
          this.isLoading.set(false);
        }
      });
  }

  // ===== ===== ===== Handle "Change is applied for" section and it's grid ===== ===== =====

  override getRowError(row: IBulkAppliedChange, rowIndex: number): string {
    const isForeignAddress = !CountriesService.isAustralianStateOrAustralia(this.form.controls.address.controls.country.value);
    const relatedChangeAddressEntity = this.bulkChangeEntities[rowIndex];

    if (isForeignAddress) {
      const soloDirectorId = row.options[0].value as string;
      const isSoleDirectorSelected = row.options.length == 1
        && relatedChangeAddressEntity.match.matches?.some((match) => match.relationshipsId.length && match.relationshipsId.includes(soloDirectorId));

      if (isSoleDirectorSelected) {
        return this.addressInfoMessage;
      }

      const forbiddenForeignAddressesLabels = [registeredAddressBulkLabel, principalAddressBulkLabel];
      const principalOrRegisteredAddressSelected = relatedChangeAddressEntity.match.matches
        ?.some((match) => forbiddenForeignAddressesLabels.includes(match.label));

      if (principalOrRegisteredAddressSelected) {
        return this.addressInfoMessage;
      }
    }

    const relatedChangeAddressEntityEstablishmentDate = relatedChangeAddressEntity.entity.dateOfEstablishment
      ? new Date(relatedChangeAddressEntity.entity.dateOfEstablishment)
      : relatedChangeAddressEntity.entity.dateOfEstablishment;
    const changeDate = this.form.controls.changeDate.value;

    if (relatedChangeAddressEntityEstablishmentDate && changeDate && changeDate <= relatedChangeAddressEntityEstablishmentDate) {
      return 'Date of change cannot be earlier or same as the company registration date';
    }

    return '';
  }
}
